import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { Common } from "../../../utils";
import "../documents/Documents.css";
import "./Payroll.css";
import { BLOCK_TYPES, createDefaultTemplate, createTextBoxBlock, defaultTextFormat, newBlockId } from "./payslipTemplateDefaults";
import PayslipTextFormatInspector, {
  getBlockDisplayText,
  getBlockTextStyle,
  supportsTextFormatting,
} from "./PayslipTextFormatInspector";
import {
  fetchPayslipPayrollData,
  finalizePayslipTemplate,
  formatMoney,
  getCompanyId,
  getCompanyName,
  loadCompanyLogo,
  loadPayslipTemplate,
  sampleAmount,
  savePayslipTemplate,
} from "./payslipTemplateApi";

const DEFAULT_LOGO = "/assets/img/logo-default.png";
const DRAG_MIME = "application/x-payslip-block";

/** @param {import('./payslipTemplateDefaults').PayslipBlock[]} blocks @param {string} id */
const moveBlock = (blocks, fromId, toId) => {
  const fromIdx = blocks.findIndex((b) => b.id === fromId);
  const toIdx = blocks.findIndex((b) => b.id === toId);
  if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return blocks;
  const next = [...blocks];
  const [item] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, item);
  return next;
};

/** @param {import('./payslipTemplateDefaults').PayslipBlock[]} blocks @param {number} index @param {import('./payslipTemplateDefaults').PayslipBlock} block */
const insertBlockAt = (blocks, index, block) => {
  const next = [...blocks];
  next.splice(Math.max(0, Math.min(index, next.length)), 0, block);
  return next;
};

const PayslipPreviewPage = () => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [companyLogo, setCompanyLogo] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [payrollData, setPayrollData] = useState({
    earnings: [],
    deductions: [],
    taxes: [],
    structures: [],
  });
  const [template, setTemplate] = useState(createDefaultTemplate());
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [paletteQuery, setPaletteQuery] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const companyId = getCompanyId();
  const currency =
    payrollData.earnings[0]?.officeCurrency ||
    payrollData.deductions[0]?.officeCurrency ||
    "";

  const selectedBlock = useMemo(
    () => template.blocks.find((b) => b.id === selectedBlockId) || null,
    [template.blocks, selectedBlockId]
  );

  const incomeTotal = useMemo(() => {
    return payrollData.earnings.reduce((sum, c) => sum + sampleAmount(c.code || c.id, 8000), 0);
  }, [payrollData.earnings]);

  const deductionTotal = useMemo(() => {
    const compSum = payrollData.deductions.reduce(
      (sum, c) => sum + sampleAmount(c.code || c.id, 800),
      0
    );
    const taxSum = payrollData.taxes.reduce(
      (sum, tx) => sum + sampleAmount(tx.id, 1200),
      0
    );
    return compSum + taxSum;
  }, [payrollData.deductions, payrollData.taxes]);

  const netPay = incomeTotal - deductionTotal;

  const textFallbacks = useMemo(
    () => ({
      [BLOCK_TYPES.CUSTOM_TEXT]: t("app.payroll.payslip.customTextPlaceholder"),
      [BLOCK_TYPES.COMPANY_NAME]: companyName || t("app.payroll.payslip.sampleCompany"),
      [BLOCK_TYPES.TITLE]: t("app.payroll.payslip.docTitle"),
      [BLOCK_TYPES.PAY_PERIOD]: t("app.payroll.payslip.periodSample"),
      [BLOCK_TYPES.DISCLAIMER]: t("app.payroll.payslip.disclaimerLive"),
    }),
    [companyName, t]
  );

  const getInspectorTextMeta = useCallback(
    (block) => {
      switch (block.type) {
        case BLOCK_TYPES.COMPANY_NAME:
          return {
            placeholder: companyName || t("app.payroll.payslip.sampleCompany"),
            hint: t("app.payroll.payslip.overrideCompanyHint"),
          };
        case BLOCK_TYPES.TITLE:
          return {
            placeholder: t("app.payroll.payslip.docTitle"),
            hint: t("app.payroll.payslip.overrideTitleHint"),
          };
        case BLOCK_TYPES.DISCLAIMER:
          return {
            placeholder: t("app.payroll.payslip.disclaimerLive"),
            hint: t("app.payroll.payslip.overrideDisclaimerHint"),
          };
        case BLOCK_TYPES.PAY_PERIOD:
          return {
            placeholder: t("app.payroll.payslip.periodSample"),
            hint: t("app.payroll.payslip.overridePeriodHint"),
          };
        default:
          return { placeholder: t("app.payroll.payslip.customTextPlaceholder"), hint: "" };
      }
    },
    [companyName, t]
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [logo, tpl, data] = await Promise.all([
        loadCompanyLogo(),
        loadPayslipTemplate(companyId),
        fetchPayslipPayrollData(),
      ]);
      setCompanyLogo(logo);
      setCompanyName(getCompanyName());
      setTemplate(tpl);
      setPayrollData(data);
      setHasUnsavedChanges(false);
    } catch {
      Common.showErrorMessage(t("app.payroll.payslip.loadError"));
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const markDirty = useCallback(() => setHasUnsavedChanges(true), []);

  const handleSaveDraft = async () => {
    if (!companyId) {
      Common.showErrorMessage(t("app.payroll.salaryComponents.companyMissing"));
      return;
    }
    setSaving(true);
    try {
      await savePayslipTemplate(companyId, template);
      setHasUnsavedChanges(false);
      Common.showSuccessMessage(t("app.payroll.payslip.draftSaved"));
    } catch {
      Common.showErrorMessage(t("app.payroll.payslip.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!companyId) {
      Common.showErrorMessage(t("app.payroll.salaryComponents.companyMissing"));
      return;
    }
    if (!window.confirm(t("app.payroll.payslip.finalizeConfirm"))) return;
    setFinalizing(true);
    try {
      const next = await finalizePayslipTemplate(companyId, template);
      setTemplate(next);
      setHasUnsavedChanges(false);
      Common.showSuccessMessage(t("app.payroll.payslip.finalizeSuccess"));
    } catch {
      Common.showErrorMessage(t("app.payroll.payslip.saveError"));
    } finally {
      setFinalizing(false);
    }
  };

  const handlePrint = useCallback(() => {
    const cleanup = () => {
      document.body.classList.remove("payroll-payslip-printing");
      window.removeEventListener("afterprint", cleanup);
    };
    document.body.classList.add("payroll-payslip-printing");
    window.addEventListener("afterprint", cleanup);
    window.print();
  }, []);

  const handleResetDefault = () => {
    if (!window.confirm(t("app.payroll.payslip.resetConfirm"))) return;
    setTemplate(createDefaultTemplate());
    setSelectedBlockId(null);
    markDirty();
  };

  const addTextBoxAt = (index) => {
    const block = createTextBoxBlock();
    setTemplate((prev) => ({ ...prev, blocks: insertBlockAt(prev.blocks, index, block) }));
    setSelectedBlockId(block.id);
    markDirty();
  };

  /** @param {DragEvent} e @param {{ type: string, componentId?: string, taxPolicyId?: string, text?: string }} payload */
  const onPaletteDragStart = (e, payload) => {
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "copy";
  };

  /** @param {DragEvent} e @param {string} blockId */
  const onCanvasBlockDragStart = (e, blockId) => {
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ reorderId: blockId }));
    e.dataTransfer.effectAllowed = "move";
  };

  /** @param {DragEvent} e @param {number} index */
  const onDropAtIndex = (e, index) => {
    e.preventDefault();
    setDragOverIndex(null);
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw) return;
    try {
      const payload = JSON.parse(raw);
      if (payload.reorderId) {
        const targetId = template.blocks[index]?.id;
        if (targetId && payload.reorderId !== targetId) {
          setTemplate((prev) => ({ ...prev, blocks: moveBlock(prev.blocks, payload.reorderId, targetId) }));
          markDirty();
        }
        return;
      }
      if (payload.type === BLOCK_TYPES.LOGO && !companyLogo) {
        Common.showErrorMessage(t("app.payroll.payslip.logoMissing"));
        return;
      }
      const block =
        payload.type === BLOCK_TYPES.CUSTOM_TEXT
          ? createTextBoxBlock()
          : {
              id: newBlockId(payload.type),
              type: payload.type,
              align: payload.align || "left",
              text: payload.text,
              componentId: payload.componentId,
              taxPolicyId: payload.taxPolicyId,
              logoWidth: payload.type === BLOCK_TYPES.LOGO ? 120 : undefined,
              ...(supportsTextFormatting(payload.type) ? defaultTextFormat() : {}),
            };
      setTemplate((prev) => ({ ...prev, blocks: insertBlockAt(prev.blocks, index, block) }));
      setSelectedBlockId(block.id);
      markDirty();
    } catch {
      /* ignore bad payload */
    }
  };

  /** @param {import('./payslipTemplateDefaults').PayslipBlock} block */
  const updateBlock = (block) => {
    setTemplate((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) => (b.id === block.id ? block : b)),
    }));
    markDirty();
  };

  const removeSelectedBlock = () => {
    if (!selectedBlockId) return;
    setTemplate((prev) => ({
      ...prev,
      blocks: prev.blocks.filter((b) => b.id !== selectedBlockId),
    }));
    setSelectedBlockId(null);
    markDirty();
  };

  /** @param {import('./payslipTemplateDefaults').PayslipBlock} block @param {string} [extraClass] */
  const renderFormattedTextBlock = (block, extraClass = "") => {
    const alignClass =
      block.align === "center"
        ? "payroll-payslip-block--center"
        : block.align === "right"
          ? "payroll-payslip-block--right"
          : "payroll-payslip-block--left";
    const displayText = getBlockDisplayText(block, textFallbacks);
    const isEmptyCustom = block.type === BLOCK_TYPES.CUSTOM_TEXT && !String(block.text || "").trim();
    return (
      <div
        className={`payroll-payslip-block ${extraClass} ${alignClass}${
          isEmptyCustom ? " payroll-payslip-block__custom--empty" : ""
        }`}
        style={getBlockTextStyle(block)}
      >
        {isEmptyCustom ? t("app.payroll.payslip.emptyTextBoxHint") : displayText}
      </div>
    );
  };

  /** @param {import('./payslipTemplateDefaults').PayslipBlock} block */
  const renderBlockContent = (block) => {
    const alignClass =
      block.align === "center"
        ? "payroll-payslip-block--center"
        : block.align === "right"
          ? "payroll-payslip-block--right"
          : "payroll-payslip-block--left";

    switch (block.type) {
      case BLOCK_TYPES.LOGO:
        return (
          <div className={`payroll-payslip-block ${alignClass}`}>
            <img
              src={companyLogo || DEFAULT_LOGO}
              alt={t("app.payroll.payslip.logoAlt")}
              className="payroll-payslip-block__logo"
              style={{ maxWidth: block.logoWidth || 120 }}
            />
          </div>
        );
      case BLOCK_TYPES.COMPANY_NAME:
        return renderFormattedTextBlock(block, "payroll-payslip-block__company");
      case BLOCK_TYPES.TITLE:
        return renderFormattedTextBlock(block, "payroll-payslip-block__title");
      case BLOCK_TYPES.PAY_PERIOD:
        return renderFormattedTextBlock(block, "payroll-payslip-block__period");
      case BLOCK_TYPES.EMPLOYEE_INFO:
        return (
          <div className={`payroll-payslip-block payroll-payslip-block__employee ${alignClass}`}>
            <div className="payroll-payslip-block__employee-row">
              <span className="payroll-payslip-block__label">{t("app.payroll.payslip.employee")}</span>
              <span>{t("app.payroll.payslip.sampleEmployeeName")}</span>
            </div>
            <div className="payroll-payslip-block__employee-row">
              <span className="payroll-payslip-block__label">{t("app.payroll.payslip.employeeId")}</span>
              <span>{t("app.payroll.payslip.sampleEmployeeId")}</span>
            </div>
            <div className="payroll-payslip-block__employee-row">
              <span className="payroll-payslip-block__label">{t("app.payroll.payslip.department")}</span>
              <span>{t("app.payroll.payslip.sampleDepartment")}</span>
            </div>
          </div>
        );
      case BLOCK_TYPES.INCOME_SECTION:
        return (
          <div className={`payroll-payslip-block payroll-payslip-block__section ${alignClass}`}>
            <div className="payroll-payslip-block__section-head">
              <i className="bi bi-arrow-up-circle-fill" aria-hidden />
              {t("app.payroll.payslip.sectionIncome")}
            </div>
            <div className="payroll-payslip-block__section-body">
              {payrollData.earnings.length === 0 ? (
                <p className="payroll-payslip-block__empty">{t("app.payroll.payslip.noEarnings")}</p>
              ) : (
                payrollData.earnings.map((c) => (
                  <div key={c.id} className="payroll-payslip__line">
                    <span>{c.name}</span>
                    <span>{formatMoney(sampleAmount(c.code || c.id, 8000), currency)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      case BLOCK_TYPES.DEDUCTION_SECTION:
        return (
          <div className={`payroll-payslip-block payroll-payslip-block__section payroll-payslip-block__section--deduction ${alignClass}`}>
            <div className="payroll-payslip-block__section-head">
              <i className="bi bi-arrow-down-circle-fill" aria-hidden />
              {t("app.payroll.payslip.sectionDeductions")}
            </div>
            <div className="payroll-payslip-block__section-body">
              {payrollData.deductions.length === 0 && payrollData.taxes.length === 0 ? (
                <p className="payroll-payslip-block__empty">{t("app.payroll.payslip.noDeductions")}</p>
              ) : (
                <>
                  {payrollData.deductions.map((c) => (
                    <div key={c.id} className="payroll-payslip__line">
                      <span>{c.name}</span>
                      <span>({formatMoney(sampleAmount(c.code || c.id, 800), currency)})</span>
                    </div>
                  ))}
                  {payrollData.taxes.map((tx) => (
                    <div key={tx.id} className="payroll-payslip__line">
                      <span>{tx.name || t("app.payroll.payslip.lineTax")}</span>
                      <span>({formatMoney(sampleAmount(tx.id, 1200), currency)})</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        );
      case BLOCK_TYPES.GROSS_TOTAL:
        return (
          <div className={`payroll-payslip__line payroll-payslip__line--subtotal ${alignClass}`}>
            <span>{t("app.payroll.payslip.lineGross")}</span>
            <span>{formatMoney(incomeTotal, currency)}</span>
          </div>
        );
      case BLOCK_TYPES.NET_PAY:
        return (
          <div className={`payroll-payslip__line payroll-payslip__line--strong ${alignClass}`}>
            <span>{t("app.payroll.payslip.lineNet")}</span>
            <span>{formatMoney(netPay, currency)}</span>
          </div>
        );
      case BLOCK_TYPES.DISCLAIMER:
        return renderFormattedTextBlock(block, "payroll-payslip-block__disclaimer");
      case BLOCK_TYPES.DIVIDER:
        return <hr className="payroll-payslip-block__divider" aria-hidden />;
      case BLOCK_TYPES.SPACER:
        return <div className="payroll-payslip-block__spacer" aria-hidden />;
      case BLOCK_TYPES.CUSTOM_TEXT:
        return renderFormattedTextBlock(block, "payroll-payslip-block__custom");
      case BLOCK_TYPES.COMPONENT_LINE: {
        const comp =
          payrollData.earnings.find((c) => c.id === block.componentId) ||
          payrollData.deductions.find((c) => c.id === block.componentId);
        const isDeduction = payrollData.deductions.some((c) => c.id === block.componentId);
        const amt = sampleAmount(comp?.code || comp?.id || block.componentId, isDeduction ? 800 : 8000);
        return (
          <div className={`payroll-payslip__line ${alignClass}`}>
            <span>{comp?.name || t("app.payroll.payslip.unknownComponent")}</span>
            <span>{isDeduction ? `(${formatMoney(amt, currency)})` : formatMoney(amt, currency)}</span>
          </div>
        );
      }
      case BLOCK_TYPES.TAX_LINE: {
        const tx = payrollData.taxes.find((x) => x.id === block.taxPolicyId);
        return (
          <div className={`payroll-payslip__line ${alignClass}`}>
            <span>{tx?.name || t("app.payroll.payslip.lineTax")}</span>
            <span>({formatMoney(sampleAmount(tx?.id || block.taxPolicyId, 1200), currency)})</span>
          </div>
        );
      }
      case BLOCK_TYPES.STRUCTURE_LABEL: {
        const st = payrollData.structures[0];
        return (
          <div className={`payroll-payslip-block payroll-payslip-block__structure ${alignClass}`}>
            <span className="payroll-payslip-block__label">{t("app.payroll.payslip.payrollStructure")}</span>
            <span>{st?.name || t("app.payroll.payslip.sampleStructure")}</span>
          </div>
        );
      }
      default:
        return null;
    }
  };

  const paletteSections = useMemo(() => {
    const q = paletteQuery.trim().toLowerCase();
    const match = (label) => !q || label.toLowerCase().includes(q);

    const layoutItems = [
      { type: BLOCK_TYPES.LOGO, label: t("app.payroll.payslip.paletteLogo"), icon: "bi-image", disabled: !companyLogo },
      { type: BLOCK_TYPES.COMPANY_NAME, label: t("app.payroll.payslip.paletteCompany"), icon: "bi-building" },
      { type: BLOCK_TYPES.TITLE, label: t("app.payroll.payslip.paletteDocTitle"), icon: "bi-type-h1" },
      { type: BLOCK_TYPES.PAY_PERIOD, label: t("app.payroll.payslip.palettePeriod"), icon: "bi-calendar3" },
      { type: BLOCK_TYPES.EMPLOYEE_INFO, label: t("app.payroll.payslip.paletteEmployee"), icon: "bi-person-badge" },
      { type: BLOCK_TYPES.STRUCTURE_LABEL, label: t("app.payroll.payslip.paletteStructure"), icon: "bi-diagram-3" },
      { type: BLOCK_TYPES.INCOME_SECTION, label: t("app.payroll.payslip.paletteIncomeSection"), icon: "bi-plus-circle" },
      { type: BLOCK_TYPES.DEDUCTION_SECTION, label: t("app.payroll.payslip.paletteDeductionSection"), icon: "bi-dash-circle" },
      { type: BLOCK_TYPES.GROSS_TOTAL, label: t("app.payroll.payslip.paletteGross"), icon: "bi-calculator" },
      { type: BLOCK_TYPES.NET_PAY, label: t("app.payroll.payslip.paletteNet"), icon: "bi-wallet2" },
      { type: BLOCK_TYPES.DISCLAIMER, label: t("app.payroll.payslip.paletteDisclaimer"), icon: "bi-info-circle" },
      { type: BLOCK_TYPES.CUSTOM_TEXT, label: t("app.payroll.payslip.paletteCustomText"), icon: "bi-text-paragraph", text: t("app.payroll.payslip.customTextPlaceholder") },
      { type: BLOCK_TYPES.DIVIDER, label: t("app.payroll.payslip.paletteDivider"), icon: "bi-hr" },
      { type: BLOCK_TYPES.SPACER, label: t("app.payroll.payslip.paletteSpacer"), icon: "bi-distribute-vertical" },
    ].filter((item) => match(item.label));

    const earningItems = payrollData.earnings
      .map((c) => ({
        type: BLOCK_TYPES.COMPONENT_LINE,
        componentId: c.id,
        label: c.name,
        icon: "bi-cash-coin",
        badge: t("app.payroll.payslip.badgeEarning"),
      }))
      .filter((item) => match(item.label));

    const deductionItems = payrollData.deductions
      .map((c) => ({
        type: BLOCK_TYPES.COMPONENT_LINE,
        componentId: c.id,
        label: c.name,
        icon: "bi-dash-lg",
        badge: t("app.payroll.payslip.badgeDeduction"),
      }))
      .filter((item) => match(item.label));

    const taxItems = payrollData.taxes
      .map((tx) => ({
        type: BLOCK_TYPES.TAX_LINE,
        taxPolicyId: tx.id,
        label: tx.name || t("app.payroll.payslip.lineTax"),
        icon: "bi-percent",
        badge: t("app.payroll.payslip.badgeTax"),
      }))
      .filter((item) => match(item.label));

    return { layoutItems, earningItems, deductionItems, taxItems };
  }, [companyLogo, paletteQuery, payrollData, t]);

  /** @param {{ type: string, label: string, icon: string, disabled?: boolean, componentId?: string, taxPolicyId?: string, text?: string, badge?: string }} item */
  const renderPaletteChip = (item) => (
    <button
      key={`${item.type}-${item.componentId || item.taxPolicyId || item.label}`}
      type="button"
      className={`payroll-payslip-palette__chip${item.disabled ? " payroll-payslip-palette__chip--disabled" : ""}`}
      draggable={!item.disabled && !previewMode}
      disabled={item.disabled || previewMode}
      title={item.disabled ? t("app.payroll.payslip.logoUploadHint") : t("app.payroll.payslip.dragHint")}
      onDragStart={(e) =>
        onPaletteDragStart(e, {
          type: item.type,
          componentId: item.componentId,
          taxPolicyId: item.taxPolicyId,
          text: item.text,
        })
      }
    >
      <i className={`bi ${item.icon}`} aria-hidden />
      <span className="payroll-payslip-palette__chip-label">{item.label}</span>
      {item.badge ? <span className="payroll-payslip-palette__chip-badge">{item.badge}</span> : null}
    </button>
  );

  const visibleBlocks = template.blocks.filter((b) => !b.hidden);

  return (
    <div className="documents-page payroll-page ts-page px-3 px-md-4 py-4 payroll-payslip-builder-page">
      <CommonPageHeader
        icon={<i className="bi bi-receipt-cutoff" />}
        title={t("app.payroll.payslip.title")}
        description={t("app.payroll.payslip.subtitle")}
        rightContent={
          <div className="d-flex flex-wrap gap-2 align-items-center">
            {template.finalized ? (
              <span className="payroll-payslip-builder__status payroll-payslip-builder__status--live">
                <i className="bi bi-patch-check-fill me-1" aria-hidden />
                {t("app.payroll.payslip.statusFinalized")}
              </span>
            ) : (
              <span className="payroll-payslip-builder__status payroll-payslip-builder__status--draft">
                <i className="bi bi-pencil-square me-1" aria-hidden />
                {t("app.payroll.payslip.statusDraft")}
              </span>
            )}
            {hasUnsavedChanges ? (
              <span className="payroll-payslip-builder__unsaved">{t("app.payroll.payslip.unsaved")}</span>
            ) : null}
            <button
              type="button"
              className={`btn btn-sm rounded-pill ${previewMode ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setPreviewMode((v) => !v)}
            >
              <i className={`bi ${previewMode ? "bi-pencil" : "bi-eye"} me-1`} aria-hidden />
              {previewMode ? t("app.payroll.payslip.editMode") : t("app.payroll.payslip.previewMode")}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" onClick={handleResetDefault} disabled={previewMode}>
              <i className="bi bi-arrow-counterclockwise me-1" aria-hidden />
              {t("app.payroll.payslip.resetDefault")}
            </button>
            <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" onClick={handleSaveDraft} disabled={saving || previewMode}>
              {saving ? t("app.payroll.common.saving") : t("app.payroll.payslip.saveDraft")}
            </button>
            <button type="button" className="btn btn-success btn-sm rounded-pill" onClick={handleFinalize} disabled={finalizing || previewMode}>
              {finalizing ? t("app.payroll.common.saving") : t("app.payroll.payslip.finalize")}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" onClick={handlePrint}>
              <i className="bi bi-printer me-1" aria-hidden />
              {t("app.payroll.payslip.print")}
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="payroll-payslip-builder__loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("app.payroll.common.loading")}</span>
          </div>
          <p>{t("app.payroll.payslip.loadingTemplate")}</p>
        </div>
      ) : (
        <div className="payroll-payslip-builder">
          {!previewMode ? (
            <aside className="payroll-payslip-builder__palette" aria-label={t("app.payroll.payslip.paletteAria")}>
              <div className="payroll-payslip-builder__palette-head">
                <h2>{t("app.payroll.payslip.paletteTitle")}</h2>
                <p>{t("app.payroll.payslip.paletteHint")}</p>
                <input
                  type="search"
                  className="form-control form-control-sm payroll-payslip-builder__search"
                  placeholder={t("app.payroll.payslip.paletteSearch")}
                  value={paletteQuery}
                  onChange={(e) => setPaletteQuery(e.target.value)}
                />
              </div>

              <div
                className="payroll-payslip-palette__textbox-drop"
                draggable
                onDragStart={(e) => onPaletteDragStart(e, { type: BLOCK_TYPES.CUSTOM_TEXT })}
              >
                <div className="payroll-payslip-palette__textbox-drop-icon">
                  <i className="bi bi-textarea-t" aria-hidden />
                </div>
                <div>
                  <strong>{t("app.payroll.payslip.dropTextBox")}</strong>
                  <p>{t("app.payroll.payslip.dropTextBoxHint")}</p>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 payroll-payslip-palette__textbox-add"
                    onClick={() => addTextBoxAt(template.blocks.length)}
                  >
                    {t("app.payroll.payslip.addTextBox")}
                  </button>
                </div>
              </div>

              {companyLogo ? (
                <div className="payroll-payslip-builder__logo-preview">
                  <img src={companyLogo} alt="" />
                  <span>{t("app.payroll.payslip.logoReady")}</span>
                </div>
              ) : (
                <div className="payroll-payslip-builder__logo-missing">
                  <i className="bi bi-image" aria-hidden />
                  <span>{t("app.payroll.payslip.logoUploadHint")}</span>
                </div>
              )}

              <div className="payroll-payslip-palette__group">
                <h3>{t("app.payroll.payslip.groupLayout")}</h3>
                <div className="payroll-payslip-palette__chips">{paletteSections.layoutItems.map(renderPaletteChip)}</div>
              </div>

              <div className="payroll-payslip-palette__group">
                <h3>{t("app.payroll.payslip.groupEarnings")}</h3>
                <div className="payroll-payslip-palette__chips">
                  {paletteSections.earningItems.length
                    ? paletteSections.earningItems.map(renderPaletteChip)
                    : <p className="payroll-payslip-palette__empty">{t("app.payroll.payslip.noEarnings")}</p>}
                </div>
              </div>

              <div className="payroll-payslip-palette__group">
                <h3>{t("app.payroll.payslip.groupDeductions")}</h3>
                <div className="payroll-payslip-palette__chips">
                  {paletteSections.deductionItems.length
                    ? paletteSections.deductionItems.map(renderPaletteChip)
                    : <p className="payroll-payslip-palette__empty">{t("app.payroll.payslip.noDeductions")}</p>}
                </div>
              </div>

              <div className="payroll-payslip-palette__group">
                <h3>{t("app.payroll.payslip.groupTaxes")}</h3>
                <div className="payroll-payslip-palette__chips">
                  {paletteSections.taxItems.length
                    ? paletteSections.taxItems.map(renderPaletteChip)
                    : <p className="payroll-payslip-palette__empty">{t("app.payroll.payslip.noTaxes")}</p>}
                </div>
              </div>

              <div className="payroll-payslip-builder__data-summary">
                <div><strong>{payrollData.earnings.length}</strong> {t("app.payroll.payslip.summaryEarnings")}</div>
                <div><strong>{payrollData.deductions.length}</strong> {t("app.payroll.payslip.summaryDeductions")}</div>
                <div><strong>{payrollData.taxes.length}</strong> {t("app.payroll.payslip.summaryTaxes")}</div>
                <div><strong>{payrollData.structures.length}</strong> {t("app.payroll.payslip.summaryStructures")}</div>
              </div>
            </aside>
          ) : null}

          <div className="payroll-payslip-builder__canvas-wrap">
            <div className="payroll-payslip-builder__canvas-toolbar">
              <span>
                <i className="bi bi-file-earmark-text me-1" aria-hidden />
                {t("app.payroll.payslip.canvasTitle")}
              </span>
              <div className="d-flex flex-wrap align-items-center gap-2">
                {!previewMode ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary rounded-pill"
                    onClick={() => addTextBoxAt(visibleBlocks.length)}
                  >
                    <i className="bi bi-plus-square me-1" aria-hidden />
                    {t("app.payroll.payslip.addTextBox")}
                  </button>
                ) : null}
                <span className="text-muted small">{t("app.payroll.payslip.canvasHint")}</span>
              </div>
            </div>

            <div
              ref={canvasRef}
              className={`payroll-payslip payroll-payslip-print payroll-payslip-builder__canvas${previewMode ? " payroll-payslip-builder__canvas--preview" : ""}`}
              onDragOver={(e) => {
                if (previewMode) return;
                e.preventDefault();
              }}
              onDrop={(e) => {
                if (previewMode) return;
                onDropAtIndex(e, visibleBlocks.length);
              }}
            >
              {visibleBlocks.length === 0 ? (
                <div
                  className="payroll-payslip-builder__dropzone payroll-payslip-builder__dropzone--empty"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => onDropAtIndex(e, 0)}
                >
                  <i className="bi bi-cloud-arrow-down" aria-hidden />
                  <p>{t("app.payroll.payslip.dropEmpty")}</p>
                </div>
              ) : (
                visibleBlocks.map((block, index) => (
                  <React.Fragment key={block.id}>
                    {!previewMode ? (
                      <div
                        className={`payroll-payslip-builder__drop-slot${dragOverIndex === index ? " is-active" : ""}`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOverIndex(index);
                        }}
                        onDragLeave={() => setDragOverIndex(null)}
                        onDrop={(e) => onDropAtIndex(e, index)}
                      />
                    ) : null}
                    <div
                      className={`payroll-payslip-builder__block${selectedBlockId === block.id && !previewMode ? " is-selected" : ""}${previewMode ? " is-readonly" : ""}`}
                      draggable={!previewMode}
                      onDragStart={(e) => onCanvasBlockDragStart(e, block.id)}
                      onClick={() => !previewMode && setSelectedBlockId(block.id)}
                      role="button"
                      tabIndex={previewMode ? -1 : 0}
                      onKeyDown={(e) => {
                        if (previewMode) return;
                        if (e.key === "Enter" || e.key === " ") setSelectedBlockId(block.id);
                      }}
                    >
                      {!previewMode ? (
                        <div className="payroll-payslip-builder__block-handle" title={t("app.payroll.payslip.dragReorder")}>
                          <i className="bi bi-grip-vertical" aria-hidden />
                        </div>
                      ) : null}
                      {renderBlockContent(block)}
                    </div>
                  </React.Fragment>
                ))
              )}
              {!previewMode && visibleBlocks.length > 0 ? (
                <div
                  className={`payroll-payslip-builder__drop-slot payroll-payslip-builder__drop-slot--tail${dragOverIndex === visibleBlocks.length ? " is-active" : ""}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverIndex(visibleBlocks.length);
                  }}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(e) => onDropAtIndex(e, visibleBlocks.length)}
                />
              ) : null}
            </div>
          </div>

          {!previewMode ? (
            <aside className="payroll-payslip-builder__inspector" aria-label={t("app.payroll.payslip.inspectorAria")}>
              <h2>{t("app.payroll.payslip.inspectorTitle")}</h2>
              {selectedBlock ? (
                <div className="payroll-payslip-inspector">
                  <p className="payroll-payslip-inspector__type">{t(`app.payroll.payslip.blockType.${selectedBlock.type}`, selectedBlock.type)}</p>

                  <label className="form-label small">{t("app.payroll.payslip.align")}</label>
                  <div className="btn-group btn-group-sm w-100 mb-3" role="group">
                    {(["left", "center", "right"]).map((align) => (
                      <button
                        key={align}
                        type="button"
                        className={`btn btn-outline-secondary${selectedBlock.align === align ? " active" : ""}`}
                        onClick={() => updateBlock({ ...selectedBlock, align })}
                      >
                        {t(`app.payroll.payslip.align${align.charAt(0).toUpperCase()}${align.slice(1)}`)}
                      </button>
                    ))}
                  </div>

                  {selectedBlock.type === BLOCK_TYPES.LOGO ? (
                    <>
                      <label className="form-label small" htmlFor="logo-width">
                        {t("app.payroll.payslip.logoWidth")}
                      </label>
                      <input
                        id="logo-width"
                        type="range"
                        min={60}
                        max={220}
                        value={selectedBlock.logoWidth || 120}
                        className="form-range mb-3"
                        onChange={(e) => updateBlock({ ...selectedBlock, logoWidth: Number(e.target.value) })}
                      />
                    </>
                  ) : null}

                  {supportsTextFormatting(selectedBlock.type) ? (
                    <PayslipTextFormatInspector
                      block={selectedBlock}
                      onChange={updateBlock}
                      t={t}
                      textPlaceholder={getInspectorTextMeta(selectedBlock).placeholder}
                      textHint={getInspectorTextMeta(selectedBlock).hint}
                    />
                  ) : null}

                  <button type="button" className="btn btn-outline-danger btn-sm w-100 rounded-pill" onClick={removeSelectedBlock}>
                    <i className="bi bi-trash me-1" aria-hidden />
                    {t("app.payroll.payslip.removeBlock")}
                  </button>
                </div>
              ) : (
                <p className="payroll-payslip-inspector__empty">{t("app.payroll.payslip.inspectorEmpty")}</p>
              )}

              <div className="payroll-payslip-inspector__tips">
                <h3>{t("app.payroll.payslip.tipsTitle")}</h3>
                <ul>
                  <li>{t("app.payroll.payslip.tip1")}</li>
                  <li>{t("app.payroll.payslip.tip2")}</li>
                  <li>{t("app.payroll.payslip.tip3")}</li>
                </ul>
              </div>
            </aside>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default PayslipPreviewPage;
